import { z } from 'zod'
import { strictify } from '~/utils/strictify'
import { createDeleteOperation } from '~/services/api/createDeleteOperation'
import { requestAndParse } from '~/services/api/requestAndParse'
import { requestGeneratedResource } from '~/services/api/requestGeneratedResource'
import { menuItemOptionStatusNarrowingSchema } from '~/services/api/dashboard-catalog/types'
import type { MenuCategoryOptionResource, MenuCategoryView } from './interfaces'
import * as operations from '../../generated'
import type {
  MenuCategoryItemOptionDto,
  MenuCategoryManagementDto,
  MenuItemOptionDto
} from '../../generated'
import { MenuCategoryStatusSchema } from '../../generated'
import { MenuCategoryStore } from '~/services/api/contracts/requests/MenuCategoryStore'
import { MenuCategoryUpdate } from '~/services/api/contracts/requests/MenuCategoryUpdate'
import { MenuCategoryAssign } from '~/services/api/contracts/requests/MenuCategoryAssign'
import { MenuCategoryAvailableMenus } from '~/services/api/contracts/requests/MenuCategoryAvailableMenus'
import { MenuCategoryIndex } from '~/services/api/contracts/requests/MenuCategoryIndex'
import { MenuCategoryOptions } from '~/services/api/contracts/requests/MenuCategoryOptions'
import { MenuCategoryUnassign } from '~/services/api/contracts/requests/MenuCategoryUnassign'

const menuCategoryManagementDtoListSchema = z.array(operations.MenuCategoryManagementDtoSchema)

const projectMenuItem = (item: MenuItemOptionDto) => menuItemOptionStatusNarrowingSchema.parse(item)

const projectCategory = (resource: MenuCategoryManagementDto): MenuCategoryView => {
  const status = MenuCategoryStatusSchema.parse(resource.status)
  const { items: sourceItems, ...category } = resource
  const items = sourceItems?.map(projectMenuItem)
  const menuId = resource.menu_ids[0]
  return {
    ...category,
    status,
    ...(menuId === undefined ? {} : { menu_id: menuId }),
    ...(items === undefined ? {} : { items, menu_items: items })
  }
}

const projectCategoryOption = (
  resource: MenuCategoryItemOptionDto
): MenuCategoryOptionResource => ({
  ...resource,
  items: resource.items.map(projectMenuItem)
})

export function useMenusCategoriesService() {
  const baseURL = `${useRuntimeConfig().public.apiBaseUrl}/api/dashboard`
  const sanctumClient = useApiRequest()
  const deleteMenuCategory = createDeleteOperation(
    sanctumClient,
    (menuId, categoryId) =>
      operations.MenuCategoryDestroyPathSchema.strict().parse({ menuId, categoryId }),
    operations.MenuCategoryDestroySuccessSchema,
    ({ menuId, categoryId }) => `${baseURL}/menus/${menuId}/categories/${categoryId}`
  )

  async function getMenuCategoriesByMenuId(
    menuId: operations.MenuCategoryIndexPath['menuId'],
    query: operations.MenuCategoryIndexQuery = {}
  ) {
    const path = operations.MenuCategoryIndexPathSchema.strict().parse({ menuId })
    const request = MenuCategoryIndex({}).parse(query)
    const response = operations.MenuCategoryIndexSuccessSchema.parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/categories`, {
        globalErrorUi: true,
        query: request
      })
    )

    return {
      data: response.data.items.map(projectCategory),
      meta: response.data.pagination
    }
  }

  async function getMenuCategoryOptionsByMenuId(
    menuId: operations.MenuCategoryOptionsPath['menuId'],
    query: operations.MenuCategoryOptionsQuery = {}
  ) {
    const path = operations.MenuCategoryOptionsPathSchema.strict().parse({ menuId })
    const request = MenuCategoryOptions({}).parse(query)
    const response = operations.MenuCategoryOptionsSuccessSchema.parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/categories/options`, {
        query: request
      })
    )

    return {
      data: response.data.items.map(projectCategoryOption),
      meta: response.data.pagination
    }
  }

  async function getAvailableMenusByMenuId(
    menuId: operations.MenuCategoryAvailableMenusPath['menuId'],
    query: operations.MenuCategoryAvailableMenusQuery = {}
  ) {
    const path = operations.MenuCategoryAvailableMenusPathSchema.strict().parse({ menuId })
    const request = MenuCategoryAvailableMenus({}).parse(query)
    const response = operations.MenuCategoryAvailableMenusSuccessSchema.parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/categories/available-menus`, {
        query: request
      })
    )

    return { data: response.data.items, meta: response.data.pagination }
  }

  async function createMenuCategory(
    menuId: operations.MenuCategoryStorePath['menuId'],
    payload: operations.MenuCategoryStoreRequest
  ) {
    const request = MenuCategoryStore({}).parse(payload)
    const response = await requestGeneratedResource(
      sanctumClient,
      {
        schema: operations.MenuCategoryStorePathSchema,
        value: { menuId },
        buildUrl: (path) => `${baseURL}/menus/${path.menuId}/categories`
      },
      operations.MenuCategoryStoreSuccessSchema,
      'POST',
      { channel: 'body', value: request }
    )

    return projectCategory(response.data)
  }

  async function updateMenuCategory(
    menuId: operations.MenuCategoryUpdatePath['menuId'],
    categoryId: operations.MenuCategoryUpdatePath['categoryId'],
    payload: operations.MenuCategoryUpdateRequest
  ) {
    const path = operations.MenuCategoryUpdatePathSchema.strict().parse({ categoryId, menuId })
    const request = MenuCategoryUpdate({}).parse(payload)
    const response = strictify(operations.MenuCategoryUpdateSuccessSchema).parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/categories/${path.categoryId}`, {
        body: request,
        method: 'PUT'
      })
    )

    return projectCategory(response.data)
  }

  async function assignMenuCategory(
    menuId: operations.MenuCategoryAssignPath['menuId'],
    categoryId: operations.MenuCategoryAssignPath['categoryId'],
    payload: operations.MenuCategoryAssignRequest = { category_ids: [categoryId] }
  ) {
    const path = operations.MenuCategoryAssignPathSchema.strict().parse({ categoryId, menuId })
    const request = MenuCategoryAssign({}).parse(payload)
    const response = operations.MenuCategoryAssignSuccessSchema.parse(
      await sanctumClient(`${baseURL}/menus/${path.menuId}/categories/${path.categoryId}/assign`, {
        body: request,
        method: 'POST'
      })
    )

    return {
      ...response,
      data: menuCategoryManagementDtoListSchema.parse(response.data).map(projectCategory)
    }
  }

  async function unassignMenuCategory(
    menuId: operations.MenuCategoryUnassignPath['menuId'],
    categoryId: operations.MenuCategoryUnassignPath['categoryId']
  ) {
    const path = operations.MenuCategoryUnassignPathSchema.strict().parse({ categoryId, menuId })
    const query = MenuCategoryUnassign({}).parse({ 'category_ids[]': [path.categoryId] })
    const response = operations.MenuCategoryUnassignSuccessSchema.parse(
      await sanctumClient(
        `${baseURL}/menus/${path.menuId}/categories/${path.categoryId}/unassign`,
        {
          method: 'DELETE',
          query
        }
      )
    )

    return {
      ...response,
      data: menuCategoryManagementDtoListSchema.parse(response.data).map(projectCategory)
    }
  }

  async function getMenuCategoryDetail(
    menuId: operations.MenuCategoryDetailPath['menuId'],
    categoryId: operations.MenuCategoryDetailPath['categoryId']
  ) {
    const path = operations.MenuCategoryDetailPathSchema.strict().parse({ categoryId, menuId })
    return (
      await requestAndParse(
        sanctumClient,
        operations.MenuCategoryDetailSuccessSchema,
        `${baseURL}/menus/${path.menuId}/categories/${path.categoryId}/detail`
      )
    ).data
  }

  return {
    assignMenuCategory,
    createMenuCategory,
    deleteMenuCategory,
    getAvailableMenusByMenuId,
    getMenuCategoriesByMenuId,
    getMenuCategoryDetail,
    getMenuCategoryOptionsByMenuId,
    unassignMenuCategory,
    updateMenuCategory
  }
}
